import { debounce } from "throttle-debounce";
import { getNode, makeid } from "../utils";
import { Dispatcher } from "./dispatcher";

const template = `
<div class="tsg-map-popup" style="visibility: hidden">
  {bar}
  <div class="tsg-map-popup__close-x"></div>
  <div class="tsg-map-popup__close"></div>
  <div class="tsg-map-popup__wrapper">
    <div class="tsg-map-popup__content"></div>
  </div>
</div>

`;

const templateBar = `
  <div class="tsg-map-popup__bar">
    <div class="tsg-map-popup__bar-content">
      <div class="tsg-map-popup__bar-open">
        <span>{bopen}</span>
        <div class="tsg-map-popup__bar-open-icon"></div>
      </div>
      <div class="tsg-map-popup__bar-close">
        <span>{bclose}</span>
        <div class="tsg-map-popup__bar-close-icon"></div>
      </div>
    </div>
  </div>
`;

export class MapPopup extends Dispatcher {
	constructor(nodeOrSelector, options = {}) {
		super();
		this.container = getNode(nodeOrSelector);
		this.options = {
			width: 320,
			mobileBreak: 650,
			callbackOpen: null,
			callbackClose: null,
			...options,
		};

		this.setupHTML();
		requestAnimationFrame(() => {
			this.setupResize();
		});
	}

	setupHTML() {
		this.container.innerHTML = template.replace(
			"{bar}",
			this.options.variant === "chart" || this.options.variant === "chart-nobar"
				? templateBar
						.replace("{bopen}", this.options.barMessage || "Als Grafik zeigen")
						.replace("{bclose}", this.options.barMessageClose || "Zurück zur Karte")
				: ""
		);
		this.elems = {
			main: this.container.querySelector(".tsg-map-popup"),
			close: this.container.querySelector(".tsg-map-popup__close"),
			closeX: this.container.querySelector(".tsg-map-popup__close-x"),
			content: this.container.querySelector(".tsg-map-popup__content"),
			wrapper: this.container.querySelector(".tsg-map-popup__wrapper"),
			barOpen: this.container.querySelector(".tsg-map-popup__bar-open"),
			barClose: this.container.querySelector(".tsg-map-popup__bar-close"),
		};

		this.isOpen = false;

		if (this.options.variant) {
			this.elems.main.classList.add(`tsg-map-popup--${this.options.variant}`);
			this.elems.barOpen.addEventListener("click", () => {
				this.open();
			});
			this.elems.barClose.addEventListener("click", () => {
				this.close();
			});
		}

		this.elems.close.addEventListener("click", () => {
			this.close();
		});
		this.elems.closeX.addEventListener("click", () => {
			this.close();
		});

		setTimeout(() => {
			this.elems.main.style.visibility = "visible";
		}, 400);
	}

	close() {
		if (!this.isOpen) return;

		if (!this.isFixed) {
			this.isOpen = false;
			this.elems.main.classList.remove("tsg-map-popup--open");
		}
		if (this.options.callbackClose) this.options.callbackClose(this.isFixed);
		this.dispatch("close");
	}

	open(fixed) {
		if (this.isOpen) return;
		this.isOpen = true;
		this.elems.main.classList.add("tsg-map-popup--open");
		if (this.options.callbackOpen) this.options.callbackOpen(fixed);
		this.elems.wrapper.scrollTop = 0;
		this.dispatch("open");
	}

	setContent(html) {
		this.elems.content.innerHTML = html;
	}

	getContentElement() {
		return this.elems.content;
	}

	setupResize() {
		this.isMobile = false;

		const checkMobile = () => {
			const w = this.container.getBoundingClientRect().width;
			this.isMobile = w <= this.options.mobileBreak;

			if (this.isMobile) {
				this.elems.main.classList.add("tsg-map-popup--mobile");
				this.elems.main.style.width = null;
			} else {
				this.elems.main.classList.remove("tsg-map-popup--mobile");
				this.elems.main.style.width = `${this.options.width}px`;
				this.elems.main.style.height = null;
			}

			if (this.options.fixedBreak && w >= this.options.fixedBreak) {
				this.elems.main.classList.add("tsg-map-popup--fixed");
				this.isFixed = true;
				this.open(true);
			} else {
				this.isFixed = false;
				this.elems.main.classList.remove("tsg-map-popup--fixed");
			}

			this.dispatch("resize");
		};

		checkMobile();

		let ww = window.innerWidth;
		window.addEventListener(
			"resize",
			debounce(200, () => {
				if (window.innerWidth !== ww) {
					ww = window.innerWidth;
					checkMobile();
				}
			})
		);
	}

	getMapPadding() {
		if (this.isMobile) {
			return {
				top: 0,
				bottom: this.elems.main.getBoundingClientRect().height * 0.8,
				left: 0,
				right: 0,
			};
		}
		return {
			top: 0,
			bottom: 0,
			left: 0,
			right: this.elems.main.getBoundingClientRect().width * 0.8,
		};
	}
}

export const specialKeys = [
	"lat",
	"latitude",
	"lon",
	"lng",
	"long",
	"longitude",
	"title",
	"subtitle",
	"label",
	"id",
	"sort",
	"heading",
	"subheading",
	"statusGroup",
	"capacityRaw",
];

export function autoPopup(d, map) {
	const ks = Object.keys(d)
		.filter((x) => specialKeys.indexOf(x) === -1)
		.filter((x) => !x.startsWith("_"));

	let html = "";

	html += `<div class="tsg-map-popup__heading">${d.title || d.heading || ""}</div>`;
	html += `<div class="tsg-map-popup__subheading">${d.subtitle || d.subheading || ""}</div>`;

	html += '<div class="tsg-map-popup__items">';

	ks.forEach((k) => {
		if (d[k] !== undefined && d[k] !== null && d[k] !== "") {
			if (k.indexOf("__text") !== -1) {
				html += `
        <div class="tsg-map-popup-item">
          <div class="tsg-map-popup-item__text tsg-font-serif">${d[k]}</div>
        </div>
        `;
			} else {
				html += `
        <div class="tsg-map-popup-item">
          <div class="tsg-map-popup-item__label">${k}</div>
          <div class="tsg-map-popup-item__value tsg-font-serif">${d[k]}</div>
        </div>
        `;
			}
		}
	});

	html += "</div>";

	if (map) {
		const id = makeid(16);
		html += `<div id="tsg-b-${id}" class="tsg-map-popup-back">Zurück zur Übersicht</div>`;
		setTimeout(() => {
			const e = document.querySelector(`#tsg-b-${id}`);
			if (e) {
				e.addEventListener("click", () => {
					map.popup.close();
					map.toInitialView(false, true);
				});
			}
		});
	}

	return html;
}
